import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  Button,
  FormCheck,
  FormControl,
  InputGroup,
  Dropdown,
  DropdownButton,
  ButtonGroup,
  Pagination,
  DropdownItem,
} from "react-bootstrap";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSortUp, faSortDown, faEllipsisVertical, faPen } from "@fortawesome/free-solid-svg-icons";
import "./DocumentTable.css";
import DocumentDeleteModal from "./DocumentDeleteModal";

const DocumentTable = ({ documents, onEdit, onMove, fetchDocuments, setShowToast, setToastInfo }) => {
  const [showExpired, setShowExpired] = useState(true); // Control if expired docs are visible
  const [filterText, setFilterText] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedContainer, setSelectedContainer] = useState("");
  const [docToBeDeleted, setDocToBeDeleted] = useState({});
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  const documentsPerPage = 10;
  const paginationRange = 20;

  const API_BASE_URL = process.env.REACT_APP_SERVER_URL;
  const API_KEY = process.env.REACT_APP_SERVER_API_KEY;

  // Enhanced formatDate function to log and validate dates properly
  const formatDate = (dateString) => {
    if (!dateString || isNaN(Date.parse(dateString))) {
      return "N/A";
    }
    const normalizedDateString = dateString.includes("T") ? dateString : `${dateString}T00:00:00.000Z`;
    const date = new Date(normalizedDateString);
    if (isNaN(date)) return "N/A";
    return date.toISOString().split("T")[0];
  };

  const isExpired = (expirationDate) => {
    if (!expirationDate) return false;
    return new Date() > new Date(expirationDate);
  };

  const handleSort = (field) => {
    if (field === "actions") return;
    const newDirection = field === sortField && sortDirection === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortDirection(newDirection);
  };

  const uniqueContainers = useMemo(() => {
    const containerNames = documents.map((doc) => doc.containerName || "");
    return [...new Set(containerNames)];
  }, [documents]);

  const sortedDocuments = useMemo(() => {
    if (!sortField) return documents;
    return [...documents].sort((a, b) => {
      // field might be top-level or in doc.metadata
      const aField = a[sortField] || a.metadata[sortField] || "";
      const bField = b[sortField] || b.metadata[sortField] || "";
      return sortDirection === "asc"
        ? aField.toString().localeCompare(bField.toString())
        : bField.toString().localeCompare(aField.toString());
    });
  }, [documents, sortField, sortDirection]);

  const filteredDocuments = useMemo(() => {
    return sortedDocuments.filter((doc) => {
      const docContainer = (doc.containerName || "").trim().toLowerCase();
      const selected = (selectedContainer || "").trim().toLowerCase();
      const matchesContainer = selected ? docContainer === selected : true;

      const searchText = filterText.toLowerCase();
      const matchesFilterText =
        doc.name.toLowerCase().includes(searchText) ||
        Object.entries(doc.metadata).some(
          ([key, val]) => key !== "actions" && val.toString().toLowerCase().includes(searchText)
        );

      // Show expired if toggled on, hide them if toggled off
      const matchesExpiration = showExpired || !isExpired(doc.metadata.expirationdate);

      return matchesContainer && matchesFilterText && matchesExpiration;
    });
  }, [sortedDocuments, showExpired, filterText, selectedContainer]);

  // If any filter changes, reset to page 1
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedContainer, filterText, showExpired]);

  const paginatedDocuments = useMemo(() => {
    const firstIndex = (currentPage - 1) * documentsPerPage;
    const lastIndex = firstIndex + documentsPerPage;
    return filteredDocuments.slice(firstIndex, lastIndex);
  }, [currentPage, filteredDocuments, documents]); // eslint-disable-line react-hooks/exhaustive-deps

  const pageCount = Math.ceil(filteredDocuments.length / documentsPerPage);

  const handleViewDownload = async (doc) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/blob/container/${doc.containerName}/blob/${doc.name}`, {
        headers: {
          "x-api-key": API_KEY,
        },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", doc.originalFileName || doc.name);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("Error downloading/viewing blob:", error);
    }
  };

  const renderSortIcon = (field) => {
    const isAsc = sortField === field && sortDirection === "asc";
    const isDesc = sortField === field && sortDirection === "desc";
    const state = isAsc ? "asc" : isDesc ? "desc" : "none";

    return (
      <span className="sort-icon" data-field={field} data-state={state} aria-hidden="true">
        <FontAwesomeIcon icon={faSortUp} className="chev up" />
        <FontAwesomeIcon icon={faSortDown} className="chev down" />
      </span>
    );
  };

  const renderPagination = () => {
    if (pageCount <= 1) return null;

    const pages = [];
    const startPage = Math.max(currentPage - Math.floor(paginationRange / 2), 1);
    const endPage = Math.min(startPage + paginationRange - 1, pageCount);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Pagination.Item
          className="paginationColor"
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="justify-content-center">
        {currentPage > 1 && (
          <Pagination.Prev className="paginationColor" onClick={() => setCurrentPage(currentPage - 1)} />
        )}
        {pages}
        {currentPage < pageCount && (
          <Pagination.Next className="paginationColor" onClick={() => setCurrentPage(currentPage + 1)} />
        )}
      </Pagination>
    );
  };

  const showDeleteConfirmation = (show, doc) => {
    setShowDeleteConfirmationModal(true);
    setDocToBeDeleted(doc);
  };

  const displayOriginalDocumentNameForTextFiles = (document) => {
    // Legacy documents are stored under a GUID blob name with the real filename
    // kept in metadata (originalFileName). New-system uploads use the real
    // filename as the blob name. Prefer the real filename for GUID-named blobs.
    const isGuidName = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(document.name);
    if (isGuidName && document.originalFileName) {
      return document.originalFileName;
    }
    if (document.originalFileName.endsWith(".txt") && !document.name.endsWith(".txt")) {
      return document.originalFileName;
    }
    return document.name;
  };

  return (
    <>
      {showDeleteConfirmationModal ? (
        <DocumentDeleteModal
          document={docToBeDeleted}
          showModal={showDeleteConfirmationModal}
          setShowModal={setShowDeleteConfirmationModal}
          fetchDocuments={fetchDocuments}
          setShowToast={setShowToast}
          setToastInfo={setToastInfo}
        />
      ) : null}
      <InputGroup className="mb-3 d-flex justify-content-between">
        <FormControl
          className="searchDocuments"
          placeholder="Search documents..."
          onChange={(e) => setFilterText(e.target.value)}
        />
        <div className="d-flex direction-row align-items-center gap-2">
          <FormCheck
            type="switch"
            id="show-expired-toggle"
            label="Show Expired"
            checked={showExpired}
            onChange={(e) => setShowExpired(e.target.checked)}
            className="ms-3"
          />
          <DropdownButton
            title={selectedContainer || "Filter by Container"}
            align="end"
            variant="light"
            className="ms-3"
            onSelect={(e) => setSelectedContainer(e)}
          >
            <Dropdown.Item className="menu-contrast" eventKey="">
              All Containers
            </Dropdown.Item>
            {uniqueContainers.map((container) => (
              <Dropdown.Item className="menu-contrast" key={container} eventKey={container}>
                {container}
              </Dropdown.Item>
            ))}
          </DropdownButton>
        </div>
      </InputGroup>

          <div className="table-vertical">
            <Table striped bordered hover className="no-wrap-cell table-sm small-text">
              <thead>
                <tr>
                  <th>Actions</th>
                  {[
                    "name",
                    "docType",
                    "propertyId",
                    "containerName",
                    "expirationDate",
                    "state",
                    "county",
                    "saleDate",
                    "saleId",
                  ].map((header) => (
                    <th key={header} onClick={() => handleSort(header)}>
                      <div className="d-flex justify-content-between w-100">
                        <span>{header.charAt(0).toUpperCase() + header.slice(1).replace(/([A-Z])/g, " $1")}</span>
                        {renderSortIcon(header)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedDocuments.length > 0 ? (
                  paginatedDocuments.map((doc, idx) => (
                    <tr
                      key={idx}
                      style={{
                        backgroundColor: isExpired(doc.metadata.expirationdate) ? "#f8d7da" : "",
                      }}
                    >
                      <td>
                        <ButtonGroup className="actions-col" size="sm">
                          <Button variant="outline-secondary" className="btn-icon" onClick={() => onEdit(doc)}>
                            <FontAwesomeIcon icon={faPen} fontSize="16" />
                          </Button>
                          <Dropdown align="end">
                            <Dropdown.Toggle className="btn-icon no-caret" variant="outline-secondary">
                              <FontAwesomeIcon icon={faEllipsisVertical} fontSize="16" />
                            </Dropdown.Toggle>
                            <Dropdown.Menu container={document.body} flip={true}>
                              <DropdownItem onClick={() => onMove(doc)}>Move</DropdownItem>
                              <DropdownItem onClick={() => handleViewDownload(doc)}>Download</DropdownItem>
                              <DropdownItem className="text-danger" onClick={() => showDeleteConfirmation(true, doc)}>
                                Delete
                              </DropdownItem>
                            </Dropdown.Menu>
                          </Dropdown>
                        </ButtonGroup>
                      </td>
                      <td className="truncate">{displayOriginalDocumentNameForTextFiles(doc)}</td>
                      <td className="truncate">{doc.metadata.doctype || "N/A"}</td>
                      <td className="truncate">{doc.metadata.propertyid || "N/A"}</td>
                      <td className="truncate">{doc.containerName || "Unknown"}</td>
                      <td className="truncate">{formatDate(doc.metadata.expirationdate)}</td>
                      <td className="truncate">{doc.metadata.state || "N/A"}</td>
                      <td className="truncate">{doc.metadata.county || "N/A"}</td>
                      <td className="truncate">{formatDate(doc.metadata.saledate)}</td>
                      <td className="truncate">{doc.metadata.saleid || "N/A"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center">
                      No documents found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
      </div>
      {renderPagination()}
    </>
  );
};

export default DocumentTable;
